/**
 * Every order-form field that HAS a Change All checkbox wired at its render site.
 *
 * A fact about the JSX, not a policy. `field_definitions.is_change_all` decides
 * whether a checkbox is offered, but it can only ever govern a checkbox that some
 * component actually renders — `<Label bulkFieldName="x">`, `<BulkFieldCheckbox
 * fieldName="x">`, or the Invoicing tab's `<InvBulkCheckbox field="x">`. Flag a field
 * with no render site and nothing happens, with no error and nothing on screen to
 * explain it. That cost a round trip on `perDiemFollowupDate`, which is why this list
 * exists.
 *
 * The third pattern was missing from the scan until the Invoicing tab's fields were
 * looked at, so this list omitted a whole tab and the Field Manager called sixteen
 * working checkboxes unreachable — including `billToContactRecno`, which is what
 * surfaced it. Two of those sixteen are the grouped approval keys (`invoiceApproval`,
 * `marginApproval`): they have a render site, which is what this list records, but
 * they fan `approvedby`/`marginapprovedby` stamps that `_NEVER_CHANGE_ALL` refuses, so
 * InvBulkCheckbox's registry gate draws nothing for them. No `field_definitions` row
 * carries either key — the rows are the stamp columns, and the Field Manager reports
 * those as blocked before it ever consults this list.
 *
 * It is here so the FIELD MANAGER can say so. The server cannot answer this — the
 * render sites are JSX, invisible to a Python service reading a table — so the answer
 * is resolved in the browser, where the order form and the Field Manager share a
 * bundle.
 *
 * KEPT HONEST BY A TEST, not by discipline: `wiredFields.test.ts` scans the frontend
 * sources for both render patterns and fails if this list disagrees. Wiring a new
 * field means running that test and pasting what it reports — never hand-editing this
 * to make a red test green, which would restore exactly the silence it exists to
 * break.
 *
 * NOT the same question as CHANGE_ALL_ELIGIBLE_FIELDS in eligibleFields.ts. That is
 * the fallback set — which fields are ON when the registry cannot answer. This is
 * which fields COULD be, whatever any flag says. `addlInfoDeleted` is the clearest
 * case of the difference: wired, and deliberately in no eligibility list at all.
 */
export const CHANGE_ALL_WIRED_FIELDS: ReadonlySet<string> = new Set([
  'addlInfoDeleted', 'allowBrokerage', 'arrivalGroundedDatetime',
  'arrivalGroundedLocation', 'availableGoodToGoDatetime', 'billToContactRecno',
  'billToRefNo', 'bobtailApproved', 'bookingNumber', 'bookingOnFileDate', 'brokerCode',
  'brokerContactRecno', 'cargoValue', 'carrierCode', 'chassisFeeResolved',
  'chassisFeeResolvedComment', 'chassisId', 'chassisProvider', 'chassisType', 'cod',
  'comments', 'commodity', 'consigneeCode', 'container', 'creditAmount', 'creditReason',
  'csr', 'cstDispatcherUsername', 'custDocCut', 'customer', 'customerCutoff',
  'customsRelDatetime', 'customsStatus', 'custTargetDate', 'cutoffDatetime',
  'dcDueDate', 'dcNumber', 'demurrageReason', 'dept', 'dispatcher',
  'documentCutoffDatetime', 'doNotBillReason', 'dropReleaseComment',
  'dropReleaseDatetime', 'equipHold', 'equipReleaseDate', 'equipReposition',
  'equipType', 'estInvoiceEmail', 'etaDate', 'examiner', 'examNo', 'examReleaseDate',
  'firstRcv', 'flexBooking', 'foodGrade', 'freightTerms', 'fullEmptyDate', 'genset',
  'gwiMove', 'hazmat', 'highValueCargo', 'hot', 'inbondNumber',
  'inbondResponsible', 'inbondRespOther', 'inbondTenderRcvd', 'inbondType',
  'invoiceApproval', 'invoiceComments', 'invoiceHoldFollowupDatetime',
  'invoiceHoldReason', 'isDaycab', 'isDrop', 'isExam', 'isGwiEquip', 'isTank', 'kgs',
  'lastFreeDay', 'lbs', 'marginApproval', 'masterPro', 'metricTons', 'moveType',
  'notOnYardReportDate', 'offhire', 'otr', 'outGateDatetime', 'packaged',
  'perDiemApproval', 'perDiemContractNumber', 'perDiemEndDate', 'perDiemFollowupDate',
  'perDiemStartDate', 'pieceCount', 'portOfDischargeCode', 'portOfOriginCode',
  'portTrace', 'preAdviseDatetime', 'priority', 'priority2', 'purchaseOrder',
  'quoteLink', 'rail', 'railbilled', 'railBillStatus', 'railLfdDate',
  'railPickupContactInfo', 'railPickupContactType', 'rateComments', 'rebill',
  'releasedSet', 'renderedDate', 'renderedDeadlineDate', 'returnChassis',
  'returnEquipment', 'returnPronumber', 'reuseOrigCode', 'reuseStatus',
  'revisedEtaDatetime', 'rolling', 'rollingFault', 'scac', 'scale',
  'schedulingContactInfo', 'schedulingContactType', 'sealNumber',
  'sendEmailNotifications', 'size', 'streetTurnHold', 'streetTurnReview', 'swing',
  'tareLbs', 'targetDeliveryDate', 'targetOnOrBy', 'temperature',
  'temperatureControlled', 'templateType', 'tempScale', 'tmwOrderNum', 'traceMode',
  'traceTerminateDatetime', 'traceVessel', 'traceVoyage', 'trainDepartDatetime',
  'ventSetting', 'vessel', 'vesselArrivalDateEst', 'vesselArrivalDatetime',
  'vesselSailDateEst', 'vesselSailDatetime', 'voyage', 'whse3461Date',
  'whseCsrUsername', 'whseEntryNum', 'whseRedStampDate', 'yardLocation',
  'yardReportDatetime', 'zeroRecordDate',
]);
